import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LANGUAGES, SUPPORTED_CODES } from '../../i18n/languages';
import './LangPicker.css';

export const LangPicker: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang: urlLang } = useParams<{ lang: string }>();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const current =
    LANGUAGES.find((l) => l.code === i18n.resolvedLanguage) ??
    LANGUAGES.find((l) => l.code === i18n.language) ??
    LANGUAGES[0];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (code: string) => {
    if (!SUPPORTED_CODES.includes(code)) return;
    i18n.changeLanguage(code);
    setOpen(false);

    // Swap the leading /:lang segment in the URL with the new one so the
    // route matches the chosen language.
    const segments = location.pathname.split('/').filter(Boolean);
    if (urlLang && SUPPORTED_CODES.includes(segments[0] ?? '')) {
      segments[0] = code;
    } else {
      segments.unshift(code);
    }
    navigate('/' + segments.join('/') + (location.search || '') + (location.hash || ''));
  };

  return (
    <div
      ref={rootRef}
      className="lp-langp"
      data-open={open ? 'true' : undefined}
    >
      <button
        type="button"
        className="lp-langp__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="lp-langp__code">
          {current.code.toUpperCase()}
        </span>
        <svg
          className="lp-langp__chev"
          width="9"
          height="9"
          viewBox="0 0 10 10"
          aria-hidden="true"
        >
          <path
            d="M1.5 3.5L5 7l3.5-3.5"
            stroke="currentColor"
            strokeWidth="1.6"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <ul className="lp-langp__menu" role="listbox">
          {LANGUAGES.map((l) => {
            const active = l.code === current.code;
            return (
              <li key={l.code} role="option" aria-selected={active}>
                <button
                  type="button"
                  className={`lp-langp__item${active ? ' is-active' : ''}`}
                  onClick={() => pick(l.code)}
                >
                  <span className="lp-langp__item-code">
                    {l.code.toUpperCase()}
                  </span>
                  <span className="lp-langp__item-name">{l.native}</span>
                  {active && (
                    <svg
                      className="lp-langp__check"
                      width="11"
                      height="11"
                      viewBox="0 0 12 12"
                      aria-hidden="true"
                    >
                      <path
                        d="M2 6.5L4.8 9.5 10 3.5"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
